// the purpose of local preferences is to abstract local storage for flags/settings/preferences
import '../interfaces/assertInterface';

import { isDevMode, requireBuildConfig } from '../config';
import { localStorageInterface } from '../interfaces/localStorageInterface';
import keys from './keys';

export { keys };

/** Returns the stored JSON value, or undefined when it is missing or unparsable. Validate it before use. */
export function getItem(key: string): unknown {
  let value: unknown;
  let jsonValue: string | null | undefined;
  try {
    jsonValue = localStorageInterface.getItem(key);
    if (jsonValue) {
      value = JSON.parse(jsonValue);
    }
  } catch (e) {
    assert(false, e, { key, jsonValue });
  }

  return value;
}

// QuotaExceededError / File error: Corrupted
const isStorageFailure = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  (('code' in error && error.code === 22) || ('name' in error && error.name === 'NS_ERROR_FILE_CORRUPTED'));

export function setItem(key: string, value: unknown): void {
  if (value === undefined) {
    removeItem(key);
  } else {
    const jsonValue = JSON.stringify(value);
    try {
      localStorageInterface.setItem(key, jsonValue);
    } catch (e) {
      assert(false, e, { key, value });
      if (isStorageFailure(e)) {
        localStorageInterface.clear();
      }
    }
  }
}

export function removeItem(key: string): void {
  try {
    localStorageInterface.removeItem(key);
  } catch (e) {
    assert(false, `can't delete local storage data for "${key}"\n${e}`);
  }
}

// Stored values are untrusted: a value of the wrong type reads as unset.
export const getBooleanItem = (key: string): boolean | undefined => {
  const value = getItem(key);
  return typeof value === 'boolean' ? value : undefined;
};

const getStringItem = (key: string): string | undefined => {
  const value = getItem(key);
  return typeof value === 'string' ? value : undefined;
};

// JSON.parse only yields plain objects, so any non-array object is one.
const isJsonObject = (value: unknown): value is { [key: string]: unknown } =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const getObjectItem = (key: string): { [key: string]: unknown } | undefined => {
  const value = getItem(key);
  return isJsonObject(value) ? value : undefined;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Getters and Setters for the Preferences
// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const getAuthSession = (): unknown => getItem(keys.authSession);
export const setAuthSession = (value: unknown): void => setItem(keys.authSession, value);
export const removeAuthSession = (): void => removeItem(keys.authSession);

export const getDebug = (): boolean | undefined => getBooleanItem(keys.debug);
export const setDebug = (value: boolean): void => setItem(keys.debug, value);
export const removeDebug = (): void => removeItem(keys.debug);

export const getPersistPurgeVersion = (): unknown => getItem(keys.persistPurgeVersion);
export const setPersistPurgeVersion = (value: unknown): void => setItem(keys.persistPurgeVersion, value);
export const removePersistPurgeVersion = (): void => removeItem(keys.persistPurgeVersion);

export function getDisableBugsnag(): boolean {
  let value = false;
  if (isDevMode()) {
    value = getBooleanItem(keys.disableBugsnag) || false;
  }
  return value;
}
export const setDisableBugsnag = (value: boolean): void => setItem(keys.disableBugsnag, value);

export const getEntitlements = (): unknown => getItem(keys.entitlements);
export const setEntitlements = (value: unknown): void => setItem(keys.entitlements, value);
export const removeEntitlements = (): void => removeItem(keys.entitlements);

export const getFeatureFlagsFreeze = (): boolean | undefined => {
  let featureFlagsFreeze: boolean | undefined = false;
  if (isDevMode()) {
    featureFlagsFreeze = getBooleanItem(keys.featureFlagsFreeze);
  }
  return featureFlagsFreeze;
};
export const setFeatureFlagsFreeze = (value: boolean): void => setItem(keys.featureFlagsFreeze, value);

export const setGetSessionStorage = (value: unknown): void => setItem(keys.getSessionStorage, value);

export function getIgnoreBugsnagReport(): boolean {
  let value = false;
  if (isDevMode()) {
    value =
      getBooleanItem(keys.ignoreBugsnagReport) || Boolean(requireBuildConfig().bugsnag_release_stage === 'development');
  }
  return value;
}
export const setIgnoreBugsnagReport = (value: boolean): void => setItem(keys.ignoreBugsnagReport, value);

export const getKeepLoggedIn = (): boolean | undefined => getBooleanItem(keys.keepLoggedIn);
export const setKeepLoggedIn = (value: boolean): void => setItem(keys.keepLoggedIn, value);

// TODO: LastSession

export const getMCPTestMode = (): unknown => {
  let mcpTestMode: unknown;
  if (isDevMode()) {
    mcpTestMode = getItem(keys.mcpTestMode);
  }
  return mcpTestMode;
};
export const setMCPTestMode = (value: unknown): void => setItem(keys.mcpTestMode, value);

export const getMobileWarningShown = (): boolean | undefined => getBooleanItem(keys.mobileWarningShown);
export const setMobileWarningShown = (value: boolean): void => setItem(keys.mobileWarningShown, value);

export const getOfflineSupport = (): boolean | undefined => getBooleanItem(keys.offlineSupport);
export const setOfflineSupport = (value: boolean): void => setItem(keys.offlineSupport, value);

export const getPersist = (): boolean => {
  let value = getBooleanItem(keys.persist) ?? false;
  if (isDevMode()) {
    value = getPersistOverride() ?? value;
  }
  return value;
};
export const setPersist = (value: boolean): void => setItem(keys.persist, value);

export const getPersistOverride = (): boolean | undefined => getBooleanItem(keys.persistOverride);
export const setPersistOverride = (value: boolean): void => setItem(keys.persistOverride, value);

export const getPollDataset = (): unknown => getItem(keys.pollDataset);
export const setPollDataset = (value: unknown): void => setItem(keys.pollDataset, value);
export const removePollDataset = (): void => removeItem(keys.pollDataset);

export const getSelectorsProfiling = (): boolean | undefined => {
  let value: boolean | undefined = false;
  if (isDevMode()) {
    value = getBooleanItem(keys.selectorsProfiling);
  }
  return value;
};
export const setSelectorsProfiling = (value: boolean): void => setItem(keys.selectorsProfiling, value);

export const setSessionStorage = (value: unknown): void => setItem(keys.sessionStorage, value);
export const removeSessionStorage = (): void => removeItem(keys.sessionStorage);

export function getSuppressAssertPopups(): boolean {
  let value = false;
  if (isDevMode()) {
    value = getBooleanItem(keys.suppressAssertPopups) || false;
  }
  return value;
}
export const setSuppressAssertPopups = (value: boolean): void => setItem(keys.suppressAssertPopups, value);

export function getSuppressWindowError(): boolean {
  let value = true;
  if (isDevMode()) {
    value = getBooleanItem(keys.suppressWindowError) || false;
  }
  return value;
}
export const setSuppressWindowError = (value: boolean): void => setItem(keys.suppressWindowError, value);

export const getWhyDidYouRender = (): boolean | undefined => {
  let whyDidYouRender: boolean | undefined = false;
  if (isDevMode()) {
    whyDidYouRender = getBooleanItem(keys.whyDidYouRender);
  }
  return whyDidYouRender;
};
export const setWhyDidYouRender = (value: boolean): void => setItem(keys.whyDidYouRender, value);

export const getWDYRSettings = (): { readonly [setting: string]: unknown } => {
  const storedSettings = getObjectItem(keys.wdyrSettings);
  return {
    collapseGroups: true,
    logOnDifferentValues: false,
    logOwnerReasons: true,
    onlyLogs: false,
    trackAllPureComponents: true,
    trackHooks: true,
    virtualIncludeAll: false,
    ...storedSettings,
  };
};
export const setWDYRSettings = (value: unknown): void => setItem(keys.wdyrSettings, value);

export const setLogoutReason = (value: string | null | undefined): void => setItem(keys.logoutReason, value);
export const getLogoutReason = (): string | undefined => getStringItem(keys.logoutReason);

export const setMobileAppOS = (value: unknown): void => setItem(keys.mobileAppOS, value);
export const getMobileAppOS = (): unknown => getItem(keys.mobileAppOS);

export const setMobileAppVersion = (value: unknown): void => setItem(keys.mobileAppVersion, value);
export const getMobileAppVersion = (): unknown => getItem(keys.mobileAppVersion);

export const setMobileAppBuildNumber = (value: unknown): void => setItem(keys.mobileBuildNumber, value);
export const getMobileAppBuildNumber = (): unknown => getItem(keys.mobileBuildNumber);
